import { Box, Button, Divider, Flex, Grid, GridItem, HStack, Image, Text, useColorMode } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { assets } from "../../../assets/assets";
import {
  ContributingEntityStatus,
  EmailInviteTypeDto,
  useDeclineContributingEntityInviteMutation,
  useGetContributingEntityInviteQuery,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { isApiErrorDto } from "../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { AlreadyAccepted } from "../AlreadyAccepted";
import { InviteNotice } from "../InviteNotice";
import { LoginDrawer } from "./LoginDrawer";
import { SignupDrawer } from "./SignupDrawer/SignupDrawer";
import { SignupOrLogin } from "./SignupOrLogin";

interface Props {
  inviteId: string;
  secret: string;
}

export const ContributingEntityEmailInvite = ({ inviteId, secret }: Props) => {
  const { t } = useTranslation();
  const theme = useColorMode();
  const displayer = useApiError();
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const { data, error } = useGetContributingEntityInviteQuery({ inviteId, secret });
  const [declineInvite, { isLoading: isDeclining }] = useDeclineContributingEntityInviteMutation();
  const [hasDeclined, setHasDeclined] = useState(false);

  if (error) {
    const code = isFetchBaseQueryError(error) && isApiErrorDto(error.data) ? error.data.errorCode : undefined;
    if (code === "ContributingEntityInviteAlreadyAccepted") return <AlreadyAccepted />;
    if (code === "ContributingEntityInviteRateLimited") {
      return (
        <InviteNotice title={t("Too many attempts")}>
          <Text>{t("Wait a few minutes, then open the link from your email again.")}</Text>
        </InviteNotice>
      );
    }
    if (code === "ContributingEntityNotFound") {
      return (
        <InviteNotice title={t("The invitation has been withdrawn")}>
          <Text>{t("The supplier who invited you has removed your company from the offer.")}</Text>
        </InviteNotice>
      );
    }
    return (
      <InviteNotice title={t("This invitation link is not valid")}>
        <Text>{t("Open the most recent link from your email, or ask the supplier to invite you again.")}</Text>
      </InviteNotice>
    );
  }
  if (!data) return <div>{t("Loading")}...</div>;

  const invitationContext = {
    emailInviteId: inviteId,
    emailInviteType: EmailInviteTypeDto.ContributingEntity,
    secret,
  };

  const isDeclined = hasDeclined || data.status === ContributingEntityStatus.InvitationDeclined;

  const decline = async () => {
    try {
      await declineInvite({
        inviteId,
        secret,
        declineContributingEntityInviteRequestDto: {},
      }).unwrap();
      setHasDeclined(true);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Box
      width="full"
      height="100vh"
      bgGradient="linear(to-br, smTealShadow, smBackground, smBackground, smBackground, smBlueShadow)"
    >
      {showSignup && (
        <SignupDrawer
          asOrg={true}
          isOpen={showSignup}
          onClose={() => setShowSignup(false)}
          invitationContext={invitationContext}
        />
      )}
      {showLogin && (
        <LoginDrawer isOpen={showLogin} onClose={() => setShowLogin(false)} invitationContext={invitationContext} />
      )}
      <Grid
        gridTemplateColumns={"1fr minmax(0, 520px) 1fr"}
        height="100%"
        gridTemplateRows={"1fr minmax(480px, auto) 2fr"}
        templateAreas={`
                "left top right"
                "left login right"
                "left bottom right"
                `}
      >
        <GridItem
          area={"login"}
          rounded="md"
          backgroundColor="smBackground"
          boxShadow="0 9px 46px 8px rgb(0,0,0,0.2)"
          padding={10}
        >
          <Flex w="100%" h="100%" flexDirection="column">
            <Flex paddingTop="20px" paddingBottom="35px">
              <Image src={theme.colorMode === "dark" ? assets.logo_dark : assets.logo} alt="Logo" h="30px" mt="10px" />
            </Flex>

            <Text fontSize="2xl">
              <b>{t("You have been invited to contribute to an offer")}</b>
            </Text>

            <Text fontSize="md" mt="20px">
              {t(
                "{{leadSupplier}} has named {{entity}} as a participant in their offer to {{buyer}}.",
                {
                  leadSupplier: data.leadSupplierName,
                  entity: data.entityName,
                  buyer: data.buyerOrganizationName,
                },
              )}
            </Text>

            <Box mt="20px" fontSize="sm" color="smMuted">
              <Text>
                <b>{t("Procurement")}:</b> {data.eventTitle}
                {data.eventReference ? ` (${data.eventReference})` : ""}
              </Text>
              <Text>
                <b>{t("Sent to")}:</b> {data.invitedEmailMasked}
              </Text>
            </Box>

            <Text fontSize="md" mt="20px">
              {t(
                "The regulations require every participating company to submit its own self-declaration (ESPD Part II and III).",
              )}
            </Text>

            <Text fontSize="sm" mt="20px" color="smMuted">
              {t("Only the person the invitation was sent to can accept it, on behalf of {{entity}}.", {
                entity: data.entityName,
              })}
            </Text>

            {isDeclined ? (
              <Text fontSize="md" mt="30px" fontWeight="semibold">
                {t("You have declined this invitation. {{leadSupplier}} will see it on their offer.", {
                  leadSupplier: data.leadSupplierName,
                })}
              </Text>
            ) : (
              <>
                <SignupOrLogin onSignup={() => setShowSignup(true)} onLogin={() => setShowLogin(true)} />
                <Divider my="4" />
                <HStack>
                  <Text fontSize="sm" color="smMuted">
                    {t("Not participating in this offer?")}
                  </Text>
                  <Button variant="link" colorScheme="red" onClick={decline} isLoading={isDeclining}>
                    {t("Decline the invitation")}
                  </Button>
                </HStack>
              </>
            )}
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
};
